import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { flushSync } from 'react-dom';
import { cn } from '@/components/ui/cn';
import { fanSize } from './fanGeometry';
import { FAN_AIM_MS, FAN_GRACE_MS, FAN_HOVER_DWELL_MS, FAN_HOVER_ENTER_MS } from './schedule';
import { ServiceFan, type FanPlate } from './ServiceFan';

/*
  ServiceFanStage — сцена «Моих сервисов» (spec-4 §2.2–2.4, §5): тёмный остров в обеих темах (data-theme,
  без dark:, R3), слева веер, справа неподвижная колонка — сводка в покое, карточка активной пластины.
  Колонка никогда не ложится на веер и не ездит: движется один индикатор — активная пластина (R23).

  Наведение мышью — с задержками: из покоя пластина становится активной через 60 мс, переход на другую —
  после 90 мс на ней; по пути к карточке (треугольник от указателя к ближним углам колонки, как
  grace-зона подменю Radix) пластины её не перехватывают; ушёл с пластин — активная держится 400 мс,
  внутри колонки — держится. Нажатие закрепляет: колонка перестаёт следовать за наведением. Касание —
  без наведения: закрепить, повторно — снять. Клавиатура — у веера (listbox), Enter ведёт к действию
  карточки, Esc в колонке — обратно на веер.

  Размер веера считается по месту (fanSize): ResizeObserver на сцене и высота видимой части холста над
  полем под плавающим поиском.
*/

export interface FanAsideApi {
  plate: FanPlate | null;
  /** Место активной пластины в порядке подключения, с нуля (−1 в покое). */
  index: number;
  total: number;
  pinned: boolean;
  unpin: () => void;
  /** Сделать пластину активной, закрепить и поставить фокус на веер («Показать» в сводке). */
  show: (id: string) => void;
  focusFan: () => void;
}

interface ServiceFanStageProps {
  plates: readonly FanPlate[];
  /** Подпись веера для читалок: «Мои сервисы: 12, включено 9». */
  label: string;
  /** Содержимое колонки: сводка (plate = null) или карточка активной пластины. */
  aside: (api: FanAsideApi) => ReactNode;
  /** Колонка при N = 0 — без оболочки карточки. */
  empty?: ReactNode;
  arriving?: readonly string[];
  /** Состав показан на экране (ServiceFan onSeen). */
  onSeen?: (ids: readonly string[]) => void;
  /** Раскрытие при показе (ServiceFan bloom). */
  bloom?: 'session' | 'never' | 'always';
  /** Секторы попадания (галерея). */
  debugSectors?: boolean;
  /**
   * viewport — веер помещается по высоте окна (страница); width — только по ширине (галерея, где сцена
   * стоит ниже первого экрана).
   */
  fit?: 'viewport' | 'width';
}

/** Поля сцены (p-6 сверху и снизу), px. */
const STAGE_PAD_Y = 48;
/** Высота колонки (h-72), px: сцена не ниже неё. */
const COLUMN_H = 288;

interface Point {
  x: number;
  y: number;
}

/** Точка внутри треугольника abc (знак площадей). */
function inTriangle(p: Point, a: Point, b: Point, c: Point): boolean {
  const s = (u: Point, v: Point, w: Point) => (u.x - w.x) * (v.y - w.y) - (v.x - w.x) * (u.y - w.y);
  const d1 = s(p, a, b);
  const d2 = s(p, b, c);
  const d3 = s(p, c, a);
  return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
}

/**
 * Сколько остаётся по высоте под верхом сцены (без учёта прокрутки) до низа видимой части ближайшего
 * прокручиваемого предка, за вычетом нижних полей предков между ними: pb-28 у Page — место под плавающим
 * поиском, сцена под него не заходит и не даёт прокрутки.
 */
function roomBelow(el: HTMLElement, top: number): number {
  let below = 0;
  for (let p = el.parentElement; p; p = p.parentElement) {
    const s = getComputedStyle(p);
    if (s.overflowY === 'auto' || s.overflowY === 'scroll') {
      const bottom = p.getBoundingClientRect().top + p.clientTop + p.clientHeight;
      return bottom - (top + p.scrollTop) - below;
    }
    below += parseFloat(s.paddingBottom) + parseFloat(s.borderBottomWidth);
  }
  return window.innerHeight - (top + window.scrollY) - below;
}

/*
  Намерение указателя и перевод фокуса — вне React: таймеры, коридор и узлы живут здесь, наружу уходит
  только итог (setActive). Наведение мышью — с задержками: из покоя пластина становится активной через
  ENTER, переход на другую — после DWELL на ней; по пути к карточке пластины её не перехватывают; ушёл
  с пластин — активная держится GRACE, внутри колонки — держится сколько угодно.
*/
class StageControl {
  /** Что под указателем сейчас (сырое попадание веера). */
  raw: string | null = null;
  fan: HTMLDivElement | null = null;
  column: HTMLDivElement | null = null;
  private active: string | null = null;
  private pinned: string | null = null;
  private side = true;
  private inColumn = false;
  private timer = 0;
  private pointer: Point = { x: 0, y: 0 };
  /** Прошлая точка указателя: вершина коридора — последняя точка на активной пластине. */
  private before: Point = { x: 0, y: 0 };
  /** Коридор к карточке; живёт до until. */
  private aim: { apex: Point; until: number } | null = null;
  private holding = false;
  /** fan — на веер, как вошёл с клавиатуры; rest — на веер в покое («К сводке»). */
  private want: 'primary' | 'fan' | 'rest' | null = null;
  /** Фокус ставится на веер в покое: «последняя активная» при входе фокуса не возвращается. */
  quiet = false;
  private readonly setActive: (id: string | null) => void;

  constructor(setActive: (id: string | null) => void) {
    this.setActive = setActive;
  }

  sync(
    active: string | null,
    pinned: string | null,
    side: boolean,
    fan: HTMLDivElement | null,
    column: HTMLDivElement | null,
  ) {
    this.fan = fan;
    this.column = column;
    this.active = active;
    this.pinned = pinned;
    this.side = side;
  }

  clear() {
    window.clearTimeout(this.timer);
    this.timer = 0;
  }

  /** Сырое попадание веера: другая пластина, кольцо или пустое место. */
  hit(id: string | null) {
    // Ушёл с активной пластины — отсюда начинается коридор к карточке.
    if (this.raw === this.active && this.active !== null && id !== this.active) {
      this.aim = { apex: { ...this.before }, until: performance.now() + FAN_AIM_MS };
    }
    this.raw = id;
    this.follow();
  }

  move(x: number, y: number) {
    this.before = this.pointer;
    this.pointer = { x, y };
    // Вышел из коридора, оставаясь на чужой пластине, — обычный переход.
    if (this.holding && !this.aiming()) {
      this.holding = false;
      this.follow();
    }
    if (this.raw !== null && this.raw === this.active) this.aim = null;
  }

  /** Указатель в колонке: активная держится, пока он там. */
  inside(yes: boolean) {
    this.inColumn = yes;
    if (yes) this.clear();
    else this.follow();
  }

  focusFan() {
    this.fan?.querySelector<SVGSVGElement>('svg[tabindex]')?.focus();
  }

  request(want: 'primary' | 'fan' | 'rest') {
    this.want = want;
    // Состояние не сменилось (то же закрепление) — рендера не будет, переводим сами.
    requestAnimationFrame(this.flush);
  }

  readonly flush = () => {
    const want = this.want;
    if (!want) return;
    this.want = null;
    if (want === 'primary') {
      this.column?.querySelector<HTMLElement>('[data-card-primary]')?.focus();
      return;
    }
    // Фокус приходит синхронно: веер сообщает о нём, пока флаг поднят.
    this.quiet = want === 'rest';
    try {
      this.focusFan();
    } finally {
      this.quiet = false;
    }
  };

  private aiming(): boolean {
    const a = this.aim;
    const col = this.column;
    if (!a || !col || performance.now() > a.until) return false;
    const r = col.getBoundingClientRect();
    const [b, c] = this.side
      ? [
          { x: r.left, y: r.top },
          { x: r.left, y: r.bottom },
        ]
      : [
          { x: r.left, y: r.top },
          { x: r.right, y: r.top },
        ];
    return inTriangle(this.pointer, a.apex, b, c);
  }

  /** Что делать с тем, что сейчас под указателем. */
  private readonly follow = () => {
    this.clear();
    if (this.pinned) return;
    const id = this.raw;
    if (id === null) {
      if (this.active === null || this.inColumn) return;
      this.timer = window.setTimeout(() => {
        if (!this.inColumn && !this.pinned && this.raw === null) this.setActive(null);
      }, FAN_GRACE_MS);
      return;
    }
    if (id === this.active) {
      this.aim = null;
      return;
    }
    if (this.active === null) {
      this.timer = window.setTimeout(() => {
        if (!this.pinned && this.raw !== null) this.setActive(this.raw);
      }, FAN_HOVER_ENTER_MS);
      return;
    }
    // По пути к карточке пластины её не перехватывают — пока указатель в коридоре и не дольше FAN_AIM_MS.
    this.holding = this.aiming();
    if (this.holding) {
      this.timer = window.setTimeout(this.follow, Math.max(0, (this.aim?.until ?? 0) - performance.now()));
      return;
    }
    this.timer = window.setTimeout(() => {
      if (!this.pinned && this.raw === id) this.setActive(id);
    }, FAN_HOVER_DWELL_MS);
  };
}

export function ServiceFanStage({
  plates,
  label,
  aside,
  empty,
  arriving,
  onSeen,
  bloom,
  debugSectors,
  fit = 'viewport',
}: ServiceFanStageProps) {
  const stageRef = useRef<HTMLElement>(null);
  const fanRef = useRef<HTMLDivElement>(null);
  const columnRef = useRef<HTMLDivElement>(null);
  const columnId = `${useId()}-column`;

  const [activeId, setActiveId] = useState<string | null>(null);
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  const n = plates.length;
  const has = (id: string | null) => id !== null && plates.some((p) => p.id === id);
  // Пластина ушла из веера — её карточки и закрепления больше нет; вернётся — придёт без них.
  if (activeId !== null && !has(activeId)) setActiveId(null);
  if (pinnedId !== null && !has(pinnedId)) setPinnedId(null);
  const active = has(activeId) ? activeId : null;
  const pinned = has(pinnedId) ? pinnedId : null;

  /* ---------- Размер ---------- */

  const [size, setSize] = useState<{ D: number; layout: 'side' | 'stacked' }>({ D: 320, layout: 'side' });
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      // Скрытая вкладка: мерить нечего, размер остаётся прежним до показа.
      if (el.getClientRects().length === 0) return;
      const W = el.clientWidth - STAGE_PAD_Y;
      const H = fit === 'viewport' ? roomBelow(el, el.getBoundingClientRect().top) - STAGE_PAD_Y : Infinity;
      const next = fanSize({ W, H });
      // Сразу, до отрисовки кадра: веер не должен мелькнуть прежним размером.
      flushSync(() => setSize((s) => (s.D === next.D && s.layout === next.layout ? s : next)));
    };
    // Первый замер ResizeObserver присылает сам — после раскладки, до отрисовки кадра.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [fit]);
  const side = size.layout === 'side';

  /* ---------- Наведение, закрепление, фокус ---------- */

  const [ctl] = useState(() => new StageControl(setActiveId));
  useLayoutEffect(() => ctl.sync(active, pinned, side, fanRef.current, columnRef.current));
  // Фокус переводится после рендера: карточки с главным действием может ещё не быть.
  useLayoutEffect(() => ctl.flush());
  useEffect(() => () => ctl.clear(), [ctl]);

  /** Активную выбрали с клавиатуры: стопкой карточку нужно показать. */
  const reveal = useRef(false);
  const onFanActive = (id: string | null, via: 'pointer' | 'keyboard') => {
    if (via === 'pointer') return ctl.hit(id);
    // Фокус вернули на веер в покое («К сводке»): последняя активная не возвращается.
    if (ctl.quiet) return;
    ctl.clear();
    setActiveId(id);
    reveal.current = id !== null;
    // Шаг с клавиатуры при закреплённой карточке переносит закрепление.
    if (pinned && id) setPinnedId(id);
  };

  const pin = (id: string | null) => {
    ctl.clear();
    setPinnedId(id);
    // Снял закрепление — активна та, что под указателем, или покой.
    setActiveId(id ?? ctl.raw);
  };

  // Стопкой карточка под веером: закреплённая и выбранная с клавиатуры должны быть видны. При наведении
  // не прокручиваем — веер уехал бы из-под указателя.
  useEffect(() => {
    const keyboard = reveal.current;
    reveal.current = false;
    if ((pinned || keyboard) && active && !side) ctl.column?.scrollIntoView({ block: 'nearest' });
  }, [ctl, active, pinned, side]);

  // Нажатие вне сцены снимает закрепление.
  useEffect(() => {
    if (!pinned) return;
    const down = (e: globalThis.PointerEvent) => {
      if (stageRef.current?.contains(e.target as Node)) return;
      setPinnedId(null);
      setActiveId(null);
    };
    document.addEventListener('pointerdown', down);
    return () => document.removeEventListener('pointerdown', down);
  }, [pinned]);

  const onStageClick = (e: MouseEvent<HTMLElement>) => {
    const t = e.target as Element;
    // Пустая сцена — снять закрепление; веер и колонка решают сами.
    if (ctl.fan?.contains(t) || ctl.column?.contains(t)) return;
    if (pinned) pin(null);
  };

  const onColumnKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || n === 0) return;
    e.preventDefault();
    e.stopPropagation();
    ctl.focusFan();
  };

  // Фокус ушёл со сцены — покой, если ничего не закреплено.
  const onBlur = (e: FocusEvent<HTMLElement>) => {
    if (stageRef.current?.contains(e.relatedTarget as Node | null)) return;
    if (!pinned) setActiveId(ctl.raw);
  };

  const api: FanAsideApi = {
    plate: plates.find((p) => p.id === active) ?? null,
    index: active ? plates.findIndex((p) => p.id === active) : -1,
    total: n,
    pinned: pinned !== null && pinned === active,
    unpin: () => {
      pin(null);
      ctl.request('rest');
    },
    show: (id) => {
      ctl.clear();
      setPinnedId(id);
      setActiveId(id);
      ctl.request('fan');
    },
    focusFan: () => ctl.focusFan(),
  };

  return (
    <section
      ref={stageRef}
      data-slot="service-fan-stage"
      data-theme="dark"
      className="rounded-xl bg-night p-6 text-petal"
      onPointerMoveCapture={(e: PointerEvent<HTMLElement>) => ctl.move(e.clientX, e.clientY)}
      onClick={onStageClick}
      onBlur={onBlur}
    >
      <div
        className={cn('flex gap-8', side ? 'flex-row items-center' : 'flex-col items-center')}
        style={side ? { height: Math.max(size.D, COLUMN_H) } : undefined}
      >
        <div ref={fanRef} className="shrink-0" style={{ width: size.D, height: size.D }}>
          <ServiceFan
            plates={plates}
            label={label}
            activeId={active}
            pinnedId={pinned}
            onActive={onFanActive}
            onPin={pin}
            onEnter={() => ctl.request('primary')}
            bloom={bloom}
            arriving={arriving}
            onSeen={onSeen}
            debugSectors={debugSectors}
            controls={columnId}
            className="size-full"
          />
        </div>
        <div
          ref={columnRef}
          id={columnId}
          className={cn('h-72 shrink-0 self-center', side ? 'w-80' : 'w-full max-w-sm scroll-mb-28')}
          onPointerEnter={() => ctl.inside(true)}
          onPointerLeave={() => ctl.inside(false)}
          onKeyDown={onColumnKeyDown}
        >
          {n === 0 ? (
            empty
          ) : (
            // Сводка ↔ карточка — растворением; карточка ↔ карточка — мгновенно (тот же key).
            <div
              key={active ? 'card' : 'summary'}
              className="flex h-full animate-fade-in flex-col gap-3 rounded-lg border border-line bg-elevated p-4 shadow-lg"
            >
              {aside(api)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
